/**
 * Module dependencies.
 */

import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import React, { ForwardedRef, forwardRef } from 'react';
import { PaginatedSelect } from './paginated-select';
import { SelectProps as AntdSelectProps } from 'antd';

/**
 * `PaginatedSelectFieldProps` type.
 */

export type PaginatedSelectFieldProps<T extends FieldValues> = UseControllerProps<T> &
  AntdSelectProps & {
    label: string;
    fieldClassName?: string;
    normalizeValue?: (newValue: string, value: string) => string;
    onLoadMore?: () => void;
    hasNextPage?: boolean;
    isFetchingNextPage?: boolean;
  };

/**
 * `PaginatedSelectFieldComponent` component.
 */

export function PaginatedSelectFieldComponent<T extends FieldValues>(
  props: PaginatedSelectFieldProps<T>,
  forwardedRef: ForwardedRef<any>
): JSX.Element {
  const {
    control,
    defaultValue,
    name,
    normalizeValue,
    rules,
    shouldUnregister,
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
    ...inputProps
  } = props;

  return (
    <Controller
      control={control}
      defaultValue={defaultValue}
      name={name}
      render={({ field: { name, onBlur, onChange, value }, fieldState: { error, invalid, isDirty, isTouched } }) => {
        return (
          <PaginatedSelect
            hasNextPage={hasNextPage}
            invalid={invalid}
            isDirty={isDirty}
            isFetchingNextPage={isFetchingNextPage}
            isTouched={isTouched}
            name={name}
            onBlur={onBlur as any}
            onLoadMore={onLoadMore}
            value={value}
            {...inputProps}
            error={error}
            onChange={newValue => {
              const finalValue = normalizeValue ? normalizeValue(String(newValue), value) : newValue;
              const result = !finalValue ? null : finalValue;

              onChange(result);

              if (props.onChange) {
                const item = inputProps.options?.find((item: any) => item.value === newValue);

                props.onChange(result, item as any);
              }
            }}
            ref={forwardedRef}
          />
        );
      }}
      rules={rules}
      shouldUnregister={shouldUnregister}
    />
  );
}

/**
 * Export `PaginatedSelectField`.
 */

export const PaginatedSelectField = forwardRef<PaginatedSelectFieldProps<any>, PaginatedSelectFieldProps<any>>(
  PaginatedSelectFieldComponent
);
